/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// Mantine
import {
	ActionIcon,
	Badge,
	Box,
	Group,
	Paper,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

// Icons
import {
	IconCheckbox,
	IconDownload,
	IconFilter,
	IconPlus,
	IconTrash,
} from '@tabler/icons-react';

// Utils
import { isEmpty } from 'lodash';

// API
import { useOrganizationsHasBrands } from '@api/get-organizations-has-entity-brands';

// Hooks
import { useAuthorization } from '@lib/authorization';

// Config
import { ROLES } from '@config/constants/roles';

// Datatable config
import { tableSchemaBase as columns } from '@features/sourcing/productions/productions.columns';
import { tableSchemaBase as columnsWithoutContact } from '@features/sourcing/productions/productionsWithContact.columns';

import { tableSchemaBase as columnsForPartner } from '@features/sourcing/productions/productionsForPartner.columns';
import { tableSchemaBase as columnsForProducer } from '@features/sourcing/productions/productionsForProducer.columns';

// Components
import { Button } from '@components/v2/ui';
import { Datatable } from '@components/v2/ui/Datatable/Datatable';
import { Pagination } from '@components/v2/ui/Pagination';

// Types
import { TTargetColumn } from '@global-types/index';

// Functions
import { getVisibleColumns } from '@utils/functions';

// Styles
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { DefaultModal } from '@components/v2/ui/DefaultModal';
import { ACLS } from '@config/constants/acls';
import { useDeleteMultipleProductions } from '@features/sourcing/productions/api/delete-multiple-productions';
import { useDownload } from '@features/sourcing/productions/api/download';
import { useProductions } from '@features/sourcing/productions/api/get-productions';
import { useMintProduction } from '@features/sourcing/productions/api/mint-production';
import { useVerifyProduction } from '@features/sourcing/productions/api/verify-production';
import { AddProduction } from '@features/sourcing/productions/components/AddProduction';
import { Details } from '@features/sourcing/productions/components/Details';
import { Filter } from '@features/sourcing/productions/components/Filter';
import ModalSelectedProductions from '@features/sourcing/productions/components/MintProductions/components/ModalSelectedProductions/ModalSelectedProductions';
import MintProductions from '@features/sourcing/productions/components/MintProductions/MintProductions';
import { useProduction } from '@features/sourcing/productions/hooks/useProduction';
import { TProduction } from '@features/sourcing/productions/types';
import { queryClient } from '@lib/react-query';
import { notifications } from '@mantine/notifications';
import useStore from '@store/index';
import { checkAclFront } from '@utils/acl-front';
import { addFooterColumn } from '@utils/datatable';
import classes from './productions.module.css';

/* -------------------------------------------------------------------------- */
/* Types                                    */
/* -------------------------------------------------------------------------- */

interface IProduction {
	id: string;
	verified: boolean;
	projectId?: string;
}

interface IProductionsResponse {
	rows: IProduction[];
	total: number;
	totalPages: number;
}

interface IVerifyVariables {
	ids: string[];
	verified: boolean;
}

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const Productions = () => {
	const { t } = useTranslation();

	const {
		setProductionsDatatableOptions,
		productionsDatatableOptions,
		productionsWithoutContactDatatableOptions,
		setProductionsSelectedRow,
		setProductionsSelectedRecords,
		getUser,
		setProductionsForPartnerDatatableOptions,
		setProductionsForProducerDatatableOptions,
		productionsForPartnerDatatableOptions,
		productionsForProducerDatatableOptions,
		isMintProductions,
		setIsMintProductions,
		setSelectedProductionsForMint,
		setSelectedProjectForProductionsMint,
		setSelectedProducerForProductionsMint,
		setCurrentStepMintProductions,
		isAddProduction,
		setIsAddProduction,
		setProductionMethod,
	} = useStore();

	const handleReset = () => {
		setIsMintProductions(false);
		setSelectedProductionsForMint([]);
		setProductionsSelectedRecords([]);
		setSelectedProjectForProductionsMint(null);
		setSelectedProducerForProductionsMint(null);
		setCurrentStepMintProductions(0);
		setIsAddProduction(false);
		setProductionMethod(null);
	};

	useEffect(() => {
		handleReset();
		return () => {
			handleReset();
		};
	}, []);

	const [
		openedFilters,
		{ open: openFilters, close: closeFilters },
	] = useDisclosure(false);

	const [
		openedSelectedProductions,
		{
			open: openeSelectedProductions,
			close: closeSelectedProductions,
		},
	] = useDisclosure(false);

	// APIs
	useOrganizationsHasBrands();

	const { checkRole, checkAcls, role } =
		useAuthorization();

	const [filtersNbr, setFiltersNbr] = useState(0);

	const [selectedProduction, setSelectedProduction] =
		useState<{
			id: string;
			projectId: string;
		} | null>(null);

	const { debouncedPageSize } = useProduction();

	const {
		productionsPage,
		productionsPageSize,
		productionsSelectedRecords,
		productionsQueryParams,
		productionsSortStatus,
		productionsSortParams,
		productionsSelectedRow,
		handlerowclicked,
		handlechangesortstatus,
		handleselectedrecordchange,
		handlepaginationchangesize,
		handlechangepagination,
		handleCloseDrawerShowMoreProduction,
		openDrawer,
		handleVerifyProduction,
		productionsOpenConfirmationModalVerify,
		handleverifyconfirmationmodalconfirm,
		handlecloseverifyconfirmationmodal,
		handledeletemultipleselectionconfirm,
		handleverifymultipleselectionconfirm,
		hasStartDate,
		handledownloadaction,
	} = useProduction();

	const deleteMultipleProductionMutation =
		useDeleteMultipleProductions({
			mutationConfig: {
				onSuccess: () => {
					queryClient.invalidateQueries({
						queryKey: [
							'productions',
							{
								page: productionsPage,
								size: debouncedPageSize,
								queryParams: productionsQueryParams,
								sortParams: productionsSortParams,
							},
						],
					});
					notifications.show({
						title: t('global.success'),
						message: t(
							'productions.notifications.productionSuccessfullyDeleted'
						),
						color: 'var(--mantine-color-igluGreen-9)',
					});
				},
				onError: (error) => {
					notifications.show({
						title: t('global.error'),
						message: t(`api.${error.type}`),
						color: 'var(--mantine-color-igluRed-9)',
					});
				},
			},
		});

	const verifyProductionMutation = useVerifyProduction({
		mutationConfig: {
			onSuccess: (
				_response,
				variables: IVerifyVariables
			) => {
				const queryKey = [
					'productions',
					{
						page: productionsPage,
						size: debouncedPageSize,
						queryParams: productionsQueryParams,
						sortParams: productionsSortParams,
					},
				];

				queryClient.setQueryData<IProductionsResponse>(
					queryKey,
					(oldData) => {
						if (!oldData || !oldData.rows) return oldData;

						return {
							...oldData,
							rows: oldData.rows.map((production) =>
								variables.ids.includes(production.id)
									? {
											...production,
											verified: variables.verified,
										}
									: production
							),
						};
					}
				);

				notifications.show({
					title: t('global.success') || 'Success',
					message: t(
						'notifications.verificationStatusSuccessfullyUpdated'
					),
					color: 'var(--mantine-color-igluGreen-9)',
					autoClose: 1500,
				});
			},
			onError(error) {
				notifications.show({
					title: 'Error',
					message: t(`api.${error.type}`),
					color: 'var(--mantine-color-igluRed-9)',
				});
			},
		},
	});

	const mintProductionMutation = useMintProduction({
		mutationConfig: {
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: [
						'productions',
						{
							page: productionsPage,
							size: debouncedPageSize,
							queryParams: productionsQueryParams,
							sortParams: productionsSortParams,
						},
					],
				});
				notifications.show({
					title: t('global.success'),
					message: t(
						'productions.notifications.productionSuccessfullyMinted'
					),
					color: 'var(--mantine-color-igluGreen-9)',
				});
				queryClient.invalidateQueries({
					queryKey: ['mint-histories'],
				});
			},
			onError(error) {
				notifications.show({
					title: t('global.error'),
					message: t(`api.${error.type}`),
					color: 'var(--mantine-color-igluRed-9)',
				});
			},
		},
	});

	const {
		data,
		isPending,
		total: totalItems,
		totalPages,
	} = useProductions(
		productionsPage,
		debouncedPageSize,
		productionsQueryParams,
		productionsSortParams
	);

	const [opened, { toggle, close }] = useDisclosure();
	const [
		openedMultipleDelete,
		{
			toggle: toggleMultipleDelete,
			close: closeMultipleDelete,
		},
	] = useDisclosure();
	const [
		openedMint,
		{ toggle: toggleMint, close: closeMint },
	] = useDisclosure();
	const [
		openedDownload,
		{ toggle: toggleDownload, close: closeDownload },
	] = useDisclosure();

	const NOT_VERIFIED_ITEM_EXIST =
		productionsSelectedRecords.findIndex(
			(item: { verified: boolean }) => !item.verified
		) !== -1;

	const IS_ENABLE_TO_DELETE =
		productionsSelectedRecords &&
		productionsSelectedRecords.length > 0 &&
		productionsSelectedRecords.findIndex(
			(item: { verified: boolean }) => item.verified
		) == -1;

	const IS_ENABLE_TO_DOWNLOAD =
		(productionsSelectedRecords &&
			productionsSelectedRecords.length > 0) ||
		(hasStartDate(productionsQueryParams) &&
			data &&
			data.length > 0);

	const download = useDownload(
		productionsPage,
		productionsPageSize,
		productionsQueryParams
	);

	const user = getUser();

	const handleDeleteOne = (
		recordToDelete: TProduction
	) => {
		const newList = productionsSelectedRecords.filter(
			(item) => item.id !== recordToDelete.id
		);

		if (newList.length === 0) {
			closeSelectedProductions();
		}

		setProductionsSelectedRecords(newList);
	};

	const handleDeleteAll = () => {
		setProductionsSelectedRecords([]);
		closeSelectedProductions();
	};

	if (isMintProductions) {
		return <MintProductions />;
	}

	if (isAddProduction) {
		return <AddProduction />;
	}

	return (
		<Paper className={classes['main-layout']}>
			<ModalSelectedProductions
				opened={openedSelectedProductions}
				onClose={() => {
					closeSelectedProductions();
				}}
				records={productionsSelectedRecords}
				onDelete={handleDeleteOne}
				onConfirm={handleDeleteAll}
			/>

			<DefaultModal
				opened={openedDownload}
				onConfirm={() => {
					handledownloadaction(user, download);
					closeDownload();
				}}
				onClose={() => {
					closeDownload();
					setProductionsSelectedRecords([]);
				}}
				title={
					productionsSelectedRecords?.length > 1
						? t('productions.confirmDownloadProductions')
						: t('productions.confirmDownloadProduction')
				}
				description={`${productionsSelectedRecords?.length || 1} ${productionsSelectedRecords?.length > 1 ? t('productions.productionsWillBeDownloaded') : t('productions.productionWillBeDownloaded')}`}
				type="warning"
			/>
			<DefaultModal
				opened={openedMint}
				onConfirm={() => {
					if (selectedProduction)
						mintProductionMutation.mutate({
							project: selectedProduction.projectId,
							productions: [selectedProduction.id],
						});
					closeMint();
				}}
				onClose={() => {
					closeMint();
					setProductionsSelectedRecords([]);
				}}
				title={t('productions.confirmMintProduction')}
				description={`${t('global.warningActionIrreversible')}`}
				type="warning"
			/>
			<DefaultModal
				opened={opened}
				onConfirm={() => {
					handleverifymultipleselectionconfirm(
						verifyProductionMutation
					);
					toggle();
				}}
				onClose={() => {
					close();
					setProductionsSelectedRecords([]);
				}}
				title={
					productionsSelectedRecords?.length > 1
						? t('productions.confirmVerifyProductions')
						: t('productions.confirmVerifyProduction')
				}
				description={`${productionsSelectedRecords?.length || 1} ${productionsSelectedRecords?.length > 1 ? t('productions.productionsWillBeVerified') : t('productions.productionWillBeVerified')}`}
				type="warning"
			/>
			<DefaultModal
				opened={openedMultipleDelete}
				onConfirm={() => {
					handledeletemultipleselectionconfirm(
						deleteMultipleProductionMutation
					);
					toggleMultipleDelete();
				}}
				onClose={() => {
					closeMultipleDelete();
					setProductionsSelectedRecords([]);
				}}
				title={
					productionsSelectedRecords?.length > 1
						? t('productions.confirmDeleteProductions')
						: t('productions.confirmDeleteProduction')
				}
				description={`${productionsSelectedRecords?.length || 1} ${productionsSelectedRecords?.length > 1 ? t('productions.productionsWillBeDeleted') : t('productions.productionWillBeDeleted')}. ${t('global.warningActionIrreversible')}`}
				type="warning"
			/>
			<Details
				opened={
					!isEmpty(productionsSelectedRow) && openDrawer
				}
				data={productionsSelectedRow}
				onClose={() => {
					handleCloseDrawerShowMoreProduction();
					setProductionsSelectedRow({});
				}}
				onVerify={() => {
					if (
						checkRole([
							ROLES.ADMIN,
							ROLES.SUPERADMIN,
							ROLES.STAFF,
						])
					) {
						handleVerifyProduction(
							productionsSelectedRow,
							verifyProductionMutation
						);
						setProductionsSelectedRow({
							...productionsSelectedRow,
							verified: !productionsSelectedRow.verified,
						});
					}
				}}
			/>
			<Filter
				opened={openedFilters}
				onClose={closeFilters}
				title={t('filters.titles.productions')}
				onApplyFilters={(filters) => {
					setFiltersNbr(Object.keys(filters).length);
				}}
				value={productionsQueryParams}
			/>
			{/**************************** Verify confirmation modal ****************************/}

			<ConfirmationModal
				title={t('productions.modal.verify.title')}
				description={t(
					'productions.modal.verify.description'
				)}
				onConfirm={() =>
					handleverifyconfirmationmodalconfirm(
						verifyProductionMutation
					)
				}
				onClose={handlecloseverifyconfirmationmodal}
				opened={
					productionsOpenConfirmationModalVerify &&
					productionsOpenConfirmationModalVerify.open
				}
			/>

			<Box className={classes['action-bar']}>
				<Button
					disabled={
						productionsSelectedRecords.length <= 0
					}
					onClick={openeSelectedProductions}
					variant="outline"
				>
					{`(${productionsSelectedRecords.length}) ${t('global.selectedProductions')}`}
				</Button>
				<Group>
					{checkAclFront(
						ACLS.FRONT.PRODUCTIONS.FILTER,
						role as ROLES
					) && (
						<div style={{ position: 'relative' }}>
							{filtersNbr > 0 && (
								<Badge
									style={{
										position: 'absolute',
										zIndex: '3',
										right: '-3px',
										fontFamily: 'InterDisplay-Regular',
									}}
									color="var(--mantine-color-igluRed-9)"
									size="xs"
									circle
								>
									{filtersNbr}
								</Badge>
							)}

							<ActionIcon
								variant="outline"
								size="48px"
								radius="xl"
								color="var(--mantine-color-igluBorder-9)"
								data-testid="bell-icon-button"
								onClick={openFilters}
								bg={
									!isEmpty(productionsQueryParams)
										? 'var(--mantine-color-igluPrimary-9)'
										: 'transparent'
								}
							>
								<IconFilter
									color={
										!isEmpty(productionsQueryParams)
											? 'var(--mantine-color-igluTextButton-9)'
											: 'var(--mantine-color-igluPrimary-9)'
									}
									radius="xl"
									size={24}
									stroke={1.5}
								/>
							</ActionIcon>
						</div>
					)}
					<Group gap={'var(--mantine-spacing-xs)'}>
						{checkAcls([ACLS.MINT_HISTORIES.CREATE]) && (
							<Button
								leftSection={
									<svg
										width="24"
										height="26"
										viewBox="0 0 24 26"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M21.6784 13.5742C21.6462 12.6566 21.1236 11.9572 20.3351 11.4991C19.7521 11.1616 19.1678 10.8241 18.5821 10.4906C17.9078 10.103 17.2321 9.72075 16.5537 9.34124C16.3724 9.23822 16.191 9.13657 16.0083 9.03491C15.1768 8.57136 14.3104 8.52528 13.4655 8.99967C11.9449 9.85494 10.431 10.7224 8.92246 11.6007C8.30052 11.9626 7.92171 12.5278 7.7726 13.2353C7.56305 14.2248 7.74305 15.2292 7.67857 16.0505C7.67857 16.2322 7.67857 16.4084 7.67723 16.5792C7.6732 17.3355 7.66379 17.9969 7.6826 18.657C7.68529 18.7478 7.69066 18.8373 7.70141 18.9254C7.78872 19.763 8.18902 20.4489 8.93858 20.8921C10.4404 21.7799 11.9597 22.6365 13.4816 23.4904C14.2701 23.9336 15.1003 23.9418 15.8834 23.5107C17.4403 22.6541 18.9851 21.769 20.5138 20.8596C21.1599 20.476 21.5776 19.8687 21.6448 19.1029C21.8073 17.2623 21.7456 15.4162 21.6784 13.5742ZM19.3129 19.5462C17.9964 20.2781 16.6867 21.0236 15.3864 21.784C14.9297 22.0523 14.528 22.0903 14.0512 21.807C13.0598 21.2174 12.0591 20.6454 11.0529 20.0788C10.7158 19.8891 10.3786 19.6993 10.0414 19.5109C9.59948 19.2642 9.42082 18.9064 9.42217 18.4157C9.42217 18.1406 9.42351 17.8668 9.42351 17.5916C9.4262 16.4179 9.42485 15.2427 9.42217 14.0676C9.42082 13.5756 9.6062 13.2191 10.0455 12.9724C11.4035 12.2093 12.7549 11.4354 14.0995 10.6492C14.5159 10.4052 14.8934 10.4134 15.3004 10.6492C15.7182 10.8905 16.1346 11.1317 16.5537 11.3703C17.2307 11.7607 17.9091 12.1497 18.5888 12.5346C18.8588 12.6864 19.1302 12.8396 19.4015 12.9914C19.8166 13.2231 19.959 13.5701 19.959 14.0134C19.9603 14.7561 19.959 15.4975 19.959 16.2389C19.959 16.9804 19.9523 17.6892 19.9617 18.413C19.9684 18.9281 19.7723 19.2913 19.3129 19.5462Z"
											fill="#D2E0F4"
										/>
										<path
											d="M16.9488 5.4312C15.2843 4.47269 13.6117 3.52375 11.9308 2.59259C10.9662 2.05933 9.95936 2.00464 8.97977 2.55157C7.21432 3.53606 5.45843 4.53696 3.70799 5.54879C2.98626 5.96583 2.54695 6.61669 2.37368 7.43299C2.13083 8.57335 2.33957 9.73149 2.26453 10.6777C2.26453 11.8126 2.24406 12.7479 2.26999 13.6818C2.30137 14.7879 2.7666 15.6931 3.72709 16.2578C4.34241 16.6188 4.96045 16.9757 5.58122 17.3298C5.58122 17.0317 5.58258 16.7296 5.58531 16.4192C5.58668 16.1361 5.5894 15.8408 5.5894 15.5277V15.4511L5.59486 15.3759C5.60305 15.2679 5.60578 15.1393 5.60305 14.9985C5.40385 14.8864 5.20603 14.777 5.00683 14.6662C4.49385 14.3805 4.28647 13.9675 4.28783 13.4028C4.29329 11.7333 4.29329 10.0624 4.28783 8.39287C4.28647 7.82542 4.50067 7.41385 5.01093 7.12944C6.58673 6.25024 8.15571 5.35873 9.71514 4.45218C10.1981 4.17051 10.6361 4.18145 11.1095 4.45218C12.1218 5.0333 13.1355 5.61442 14.1519 6.18871C14.3061 6.17093 14.4616 6.16273 14.6172 6.16273C15.3867 6.16273 16.1493 6.3692 16.8833 6.77393C17.4072 7.06517 17.9448 7.36462 18.4987 7.67638C18.4127 6.69189 17.822 5.93438 16.9488 5.4312Z"
											fill="#D2E0F4"
										/>
									</svg>
								}
								variant="primary"
								onClick={() => {
									setIsMintProductions(true);
								}}
							>
								{t('global.startMint')}
							</Button>
						)}
						{checkAcls([ACLS.PRODUCTIONS.CREATE]) && (
							<Button
								onClick={() => setIsAddProduction(true)}
								leftSection={
									<IconPlus
										radius="xl"
										size={24}
										stroke={1.5}
									/>
								}
								variant="primary"
							>
								{t('productions.addProduction')}
							</Button>
						)}
					</Group>
				</Group>
			</Box>

			<Box className={classes['action-row-bar']}>
				<Group gap={'var(--mantine-spacing-xs)'}>
					{checkAcls([ACLS.PRODUCTIONS.ADMIN]) && (
						<Button
							leftSection={
								<IconCheckbox
									radius="xl"
									size={24}
									stroke={1.5}
								/>
							}
							variant="outline"
							disabled={!NOT_VERIFIED_ITEM_EXIST}
							onClick={toggle}
						>
							{t('global.verify')}
						</Button>
					)}
					{checkAcls([
						ACLS.PRODUCTIONS.DELETE_MULTIPLE,
					]) && (
						<Button
							leftSection={
								<IconTrash
									radius="xl"
									size={24}
									stroke={1.5}
								/>
							}
							variant="outline"
							disabled={!IS_ENABLE_TO_DELETE}
							onClick={() => {
								toggleMultipleDelete();
							}}
						>
							{t('global.delete')}
						</Button>
					)}
				</Group>
				<Button
					onClick={() => {
						toggleDownload();
					}}
					disabled={!IS_ENABLE_TO_DOWNLOAD}
					variant="outline"
					leftSection={
						<IconDownload
							radius="xl"
							size={24}
							stroke={1.5}
						/>
					}
				>
					{t('global.download')}
				</Button>
			</Box>

			<Box className={classes['container-table']}>
				{Array.isArray(data) && (
					<Datatable
						height={'100%'}
						onDelete={(row: any) => {
							setProductionsSelectedRecords([
								...productionsSelectedRecords,
								row,
							]);

							toggleMultipleDelete();
						}}
						onVerify={(row: any) => {
							if (
								checkRole([
									ROLES.ADMIN,
									ROLES.SUPERADMIN,
									ROLES.STAFF,
								])
							) {
								handleVerifyProduction(
									row,
									verifyProductionMutation
								);
							}
						}}
						onMint={(row: any) => {
							setSelectedProduction(row);
							toggleMint();
						}}
						fetching={isPending}
						onRowClick={handlerowclicked}
						handleUpdateColumns={(
							cols: TTargetColumn[]
						) => {
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? setProductionsDatatableOptions(
										getVisibleColumns(columns, cols)
									)
								: checkRole([ROLES.ORGANIZATION_ADMIN])
									? setProductionsForPartnerDatatableOptions(
											getVisibleColumns(
												columnsForPartner,
												cols
											)
										)
									: checkRole([
												ROLES.PRODUCER_ADMIN,
												ROLES.PRODUCER_STAFF,
										  ])
										? setProductionsForProducerDatatableOptions(
												getVisibleColumns(
													columnsForProducer,
													cols
												)
											)
										: setProductionsDatatableOptions(
												getVisibleColumns(columns, cols)
											);
						}}
						allColumns={
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? columns
								: checkRole([ROLES.ORGANIZATION_ADMIN])
									? columnsForPartner
									: checkRole([
												ROLES.PRODUCER_ADMIN,
												ROLES.PRODUCER_STAFF,
										  ])
										? columnsForProducer
										: columnsWithoutContact
						}
						columns={addFooterColumn(
							data,
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? productionsDatatableOptions
								: checkRole([ROLES.ORGANIZATION_ADMIN])
									? productionsForPartnerDatatableOptions
									: checkRole([
												ROLES.PRODUCER_ADMIN,
												ROLES.PRODUCER_STAFF,
										  ])
										? productionsForProducerDatatableOptions
										: productionsWithoutContactDatatableOptions
						)}
						records={data}
						selectedRecords={
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							])
								? productionsSelectedRecords
								: null
						}
						onSelectedRecordsChange={
							handleselectedrecordchange
						}
						onSortStatusChange={handlechangesortstatus}
						sortstatus={productionsSortStatus}
					/>
				)}
			</Box>

			<Box className={classes['pagination-bar']}>
				<Pagination
					maxPageSize={1000}
					pageSize={productionsPageSize}
					totalItems={totalItems}
					activePage={productionsPage}
					onPageSizeChange={handlepaginationchangesize}
					onChange={handlechangepagination}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Paper>
	);
};

export default Productions;
